/**
 * Coaching & Accountability v1 — thin client wrapper.
 *
 * Mirrors the established services/* style (apiRequest under the hood,
 * each export does one thing). Every call sends companyId implicitly via
 * the auth cookie — never as a query/body field.
 */

import { apiRequest } from './apiClient';
import { notifyCoachingUpdated } from '../utils/directorEvents';

export async function listNotesForSubject({
  employeeProfileId = null,
  managerId = null,
  includeArchived = false,
  limit = 50
} = {}) {
  const params = new URLSearchParams();
  if (employeeProfileId) params.set('employeeProfileId', String(employeeProfileId));
  if (managerId) params.set('managerId', String(managerId));
  if (includeArchived) params.set('includeArchived', 'true');
  if (limit) params.set('limit', String(limit));
  const res = await apiRequest(`/api/coaching/notes?${params.toString()}`);
  return Array.isArray(res?.notes) ? res.notes : [];
}

export async function listNotesForManager(managerId, { includeArchived = false, limit = 50 } = {}) {
  return listNotesForSubject({ managerId, includeArchived, limit });
}

// B11 helper — every mutation calls this so subscribers (nav badge, exec
// snapshot chip, follow-up widget, manager-side widget) refetch.
function broadcastCoachingChange(kind, payload) {
  notifyCoachingUpdated({ kind, payload });
}

export async function createNote({
  employeeProfileId = null,
  managerId = null,
  visibility = 'leadership',
  body,
  periodAnchor = null,
  contextSnapshot = null
}) {
  const payload = {
    body,
    visibility,
    periodAnchor,
    contextSnapshot
  };
  if (employeeProfileId) payload.employeeProfileId = employeeProfileId;
  if (managerId) payload.managerId = managerId;
  const result = await apiRequest('/api/coaching/notes', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  broadcastCoachingChange('note.created', {
    employeeProfileId,
    managerId: result?.managerId || managerId || null
  });
  return result;
}

export async function updateNote(noteId, updates) {
  const result = await apiRequest(`/api/coaching/notes/${noteId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates)
  });
  broadcastCoachingChange('note.updated', { noteId });
  return result;
}

export async function deleteNote(noteId) {
  const result = await apiRequest(`/api/coaching/notes/${noteId}`, { method: 'DELETE' });
  broadcastCoachingChange('note.deleted', { noteId });
  return result;
}

export async function createActionItem({ managerId, ownerUserId, title, body = null, dueDate = null, coachingNoteId = null }) {
  const result = await apiRequest('/api/coaching/action-items', {
    method: 'POST',
    body: JSON.stringify({ managerId, ownerUserId, title, body, dueDate, coachingNoteId })
  });
  broadcastCoachingChange('action-item.created', { managerId });
  return result;
}

export async function updateActionItem(actionItemId, updates) {
  const result = await apiRequest(`/api/coaching/action-items/${actionItemId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates)
  });
  broadcastCoachingChange('action-item.updated', { actionItemId, status: updates?.status });
  return result;
}

export async function deleteActionItem(actionItemId) {
  const result = await apiRequest(`/api/coaching/action-items/${actionItemId}`, { method: 'DELETE' });
  broadcastCoachingChange('action-item.deleted', { actionItemId });
  return result;
}

// W5 — Coaching tab in Workforce Hub. Reads open and completed
// action items for a given manager scope. Server enforces tenant
// scope from req.user; the client never sends companyId.
export async function listActionItemsForManager(managerId, { status = null, limit = 50 } = {}) {
  const params = new URLSearchParams({ managerId: String(managerId) });
  if (status) params.set('status', status);
  if (limit) params.set('limit', String(limit));
  const res = await apiRequest(`/api/coaching/action-items?${params.toString()}`);
  return Array.isArray(res?.items) ? res.items : [];
}

export async function listMyOpenItems({ limit = 50 } = {}) {
  const res = await apiRequest(`/api/coaching/me/open-items?limit=${limit}`);
  return Array.isArray(res?.items) ? res.items : [];
}

export async function listFollowUps({ limit = 50 } = {}) {
  const res = await apiRequest(`/api/coaching/follow-ups?limit=${limit}`);
  return Array.isArray(res?.items) ? res.items : [];
}

export async function getFollowUps({ limit = 50 } = {}) {
  const res = await apiRequest(`/api/coaching/follow-ups?limit=${limit}`);
  return {
    items: Array.isArray(res?.items) ? res.items : [],
    counts: {
      total: Number(res?.counts?.total || 0),
      overdue: Number(res?.counts?.overdue || 0),
      dueToday: Number(res?.counts?.dueToday || 0),
      dueThisWeek: Number(res?.counts?.dueThisWeek || 0)
    }
  };
}

export async function getFollowUpCounts() {
  const res = await getFollowUps({ limit: 1 });
  return res.counts;
}
